import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Plus, MapPin, Mail, Phone, Users, ClipboardList,
  Trash2, ArrowLeft, AlertTriangle,
} from 'lucide-react';

export default function AdminCentersPage() {
  const { lang } = useApp();
  const [centers, setCenters] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ name_ar: '', name_en: '', location: '', contact_email: '', contact_phone: '' });

  const reload = useCallback(() => {
    api.get('/admin/centers').then(r => setCenters(r.data));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const submit = async () => {
    if (!form.name_ar) {
      toast.error(lang === 'ar' ? 'الاسم العربي مطلوب' : 'Arabic name required');
      return;
    }
    try {
      await api.post('/admin/centers', form);
      toast.success(lang === 'ar' ? 'تم إنشاء المركز' : 'Center created');
      setOpen(false);
      setForm({ name_ar: '', name_en: '', location: '', contact_email: '', contact_phone: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'error'); }
  };

  const removeCenter = async () => {
    try {
      await api.delete(`/admin/centers/${confirmDelete.id}`);
      toast.success(lang === 'ar' ? 'تم الحذف' : 'Deleted');
      setConfirmDelete(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'error'); }
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-centers-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/admin"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'لوحة الإدارة' : 'Admin'}</Link>
      </Button>

      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-cyan-100 grid place-items-center"><Building2 className="w-6 h-6 text-cyan-700" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'المراكز والمدارس' : 'Centers / schools'}</h1>
            <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'إدارة multi-tenant — كل مركز ببياناته وإحصاءاته.' : 'Multi-tenant — each center with its data & stats.'}</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-center-btn">
          <Plus className="w-4 h-4 me-1" />{lang === 'ar' ? 'مركز جديد' : 'New center'}
        </Button>
      </div>

      {centers.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-3">{lang === 'ar' ? 'لا توجد مراكز بعد. أضف مركزاً لتمكين النظام متعدد المستأجرين.' : 'No centers yet. Add one to enable multi-tenant.'}</p>
          <Button onClick={() => setOpen(true)} className="bg-cyan-700" data-testid="new-center-empty">{lang === 'ar' ? 'إنشاء أول مركز' : 'Create first center'}</Button>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {centers.map(c => (
            <Card key={c.id} className="card-elevate" data-testid={`center-card-${c.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-5 h-5 text-cyan-700 shrink-0" />
                    <CardTitle className="text-base line-clamp-1">{c.name_ar}</CardTitle>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(c)} className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`delete-center-${c.id}`} aria-label="delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {c.name_en && <div className="text-xs text-muted-foreground">{c.name_en}</div>}
              </CardHeader>
              <CardContent className="pt-1 space-y-1.5 text-xs">
                {c.location && <div className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="w-3 h-3" />{c.location}</div>}
                {c.contact_email && <div className="flex items-center gap-1.5 text-muted-foreground line-clamp-1"><Mail className="w-3 h-3" />{c.contact_email}</div>}
                {c.contact_phone && <div className="flex items-center gap-1.5 text-muted-foreground"><Phone className="w-3 h-3" />{c.contact_phone}</div>}
                <div className="flex items-center gap-2 pt-2 border-t border-border mt-2">
                  <Badge variant="secondary" className="text-[10px]"><Users className="w-3 h-3 me-1" />{c.student_count || 0} {lang === 'ar' ? 'طالب' : 'students'}</Badge>
                  <Badge variant="secondary" className="text-[10px]"><ClipboardList className="w-3 h-3 me-1" />{c.iep_count || 0} {lang === 'ar' ? 'خطة' : 'IEPs'}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New center dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-center-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'ar' ? 'مركز جديد' : 'New center'}</DialogTitle>
            <DialogDescription>{lang === 'ar' ? 'سيُستخدم لربط الطلاب والخطط بهذا المركز.' : 'Used to link students & IEPs to this center.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">{lang === 'ar' ? 'الاسم بالعربية *' : 'Arabic name *'}</Label><Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="center-name-ar" /></div>
            <div><Label className="text-xs">{lang === 'ar' ? 'الاسم بالإنجليزية' : 'English name'}</Label><Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="center-name-en" /></div>
            <div><Label className="text-xs">{lang === 'ar' ? 'الموقع' : 'Location'}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="center-location" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="center-email" /></div>
              <div><Label className="text-xs">{lang === 'ar' ? 'الهاتف' : 'Phone'}</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="center-phone" /></div>
            </div>
            <Button onClick={submit} className="w-full bg-cyan-700 hover:bg-cyan-800" data-testid="save-center-btn">{lang === 'ar' ? 'إنشاء' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="delete-center-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2"><AlertTriangle className="w-5 h-5" />{lang === 'ar' ? 'حذف المركز' : 'Delete center'}</DialogTitle>
            <DialogDescription>{confirmDelete?.name_ar}</DialogDescription>
          </DialogHeader>
          <p className="text-sm">{lang === 'ar' ? 'لن يُحذف الطلاب أو الخطط المرتبطة، لكن سيتم إلغاء الربط بالمركز.' : 'Linked students and IEPs are NOT removed, but center linkage will be cleared.'}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="flex-1">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant="destructive" onClick={removeCenter} className="flex-1" data-testid="confirm-delete-center"><Trash2 className="w-4 h-4 me-1" />{lang === 'ar' ? 'حذف' : 'Delete'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
